import { ChevronLeft } from "lucide-react";
import { Link } from "react-router";
import { useActiveOrgLogo, useActiveTeamName } from "../../lib/use-active-org.js";
import { AccountMenu } from "./account-menu.js";
import { ExpiryGlyph } from "./expiry-glyph.js";

interface HomeContent {
  /** Club identity panel rendered on the left. */
  crest: string;
  /** Optional URL to a real team crest / logo image. Falls back to initials. */
  crestUrl?: string | null;
  teamName: string;
  meta: string;
}

interface PageContent {
  /** Page title rendered next to the back chevron. */
  title: string;
  /** Tiny secondary line (usually the club name). Optional. */
  subtitle?: string;
  /** Custom back target. Defaults to `/`. */
  backTo?: string;
}

type AppHeaderProps = ({ mode: "home" } & HomeContent) | ({ mode: "page" } & PageContent);

function isTauri(): boolean {
  return (
    !!(window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__ ||
    !!(window as unknown as { __TAURI__?: unknown }).__TAURI__
  );
}

/**
 * Unified header — the only chrome the app ships. Two modes:
 *
 *   - `home`: shows club crest + name + meta (league / next match).
 *   - `page`: shows back chevron + page title (+ optional subtitle).
 *
 * The right-side cluster collapses to a single Account dropdown which
 * surfaces team/club switchers and sign-out. The standalone Power button
 * was removed in v2 — sign-out lives only inside the dropdown so users
 * stop accidentally quitting the desktop app.
 *
 * Sync is implicit (runs automatically in the background — see SyncProvider).
 */
export function AppHeader(props: AppHeaderProps) {
  return (
    <header
      className="glass-floating relative z-20 flex items-center gap-4 rounded-3xl px-4 py-3 sm:px-5 sm:py-4"
      style={{ minHeight: props.mode === "home" ? 88 : 72 }}
    >
      {props.mode === "home" ? <HomeIdentity {...props} /> : <PageHeading {...props} />}

      <div className="ml-auto flex items-center gap-2">
        {/* Billing warning sits left of the menu and renders nothing when
            there is nothing to warn about — so it never reserves space. */}
        <ExpiryGlyph />
        <AccountMenu />
      </div>
    </header>
  );
}

function HomeIdentity({ crest, crestUrl, teamName, meta }: HomeContent) {
  const initials = computeCrestInitials(crest);
  return (
    <>
      <div
        className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-2xl sm:size-14"
        style={{
          background: crestUrl
            ? "rgba(255,255,255,0.04)"
            : "linear-gradient(140deg, rgba(151,252,0,0.22) 0%, rgba(151,252,0,0.06) 100%)",
          border: "1px solid rgba(151,252,0,0.35)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 24px -10px rgba(151,252,0,0.45)",
        }}
        aria-hidden
      >
        {crestUrl ? (
          <img src={crestUrl} alt="" className="size-full object-cover" />
        ) : (
          <span
            className="home-display"
            style={{
              fontWeight: 800,
              fontSize: 18,
              letterSpacing: 0.4,
              color: "var(--color-pitch-lime)",
            }}
          >
            {initials}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div
          className="home-display truncate text-[17px] font-bold sm:text-[20px]"
          style={{ color: "#f4f5f3" }}
        >
          {teamName}
        </div>
        <div
          className="truncate text-[11px] uppercase tracking-[0.14em] sm:text-[12px]"
          style={{ color: "var(--color-on-surface-variant)" }}
        >
          {meta}
        </div>
      </div>
    </>
  );
}

/** Compute up to two-letter initials from a team / club name. */
function computeCrestInitials(source: string): string {
  const parts = source
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  }
  return parts[0]!.slice(0, 2).toUpperCase();
}

function PageHeading({ title, subtitle, backTo }: PageContent) {
  const target = backTo ?? "/";
  // Active club crest — same source as the home cockpit header so the team
  // identity is visible on every page next to the back chevron.
  const orgLogo = useActiveOrgLogo();
  // The subtitle slot now defaults to the active team name ("Coach → Team"
  // is the active context model). Callers can still override via `subtitle`
  // for special pages (e.g. account/club show the club name).
  const activeTeamName = useActiveTeamName();
  const resolvedSubtitle = subtitle ?? activeTeamName ?? "";
  const crestSource = resolvedSubtitle;
  const crestInitials = computeCrestInitials(crestSource);

  return (
    <>
      <Link
        to={target}
        aria-label="Zurück"
        className="grid size-12 place-items-center rounded-2xl border transition-colors duration-fast"
        style={{
          borderColor: "rgba(255,255,255,0.10)",
          background: "rgba(255,255,255,0.04)",
          color: "var(--color-on-surface)",
        }}
      >
        <ChevronLeft className="size-5" aria-hidden />
      </Link>

      {/* Team crest — logo image when available, otherwise initials of the
          active club name (mirrors the home cockpit treatment). */}
      {(orgLogo || crestSource) && (
        <div
          className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-2xl"
          style={{
            background: orgLogo
              ? "rgba(255,255,255,0.04)"
              : "linear-gradient(140deg, rgba(151,252,0,0.22) 0%, rgba(151,252,0,0.06) 100%)",
            border: "1px solid rgba(151,252,0,0.35)",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 18px -10px rgba(151,252,0,0.45)",
          }}
          aria-hidden
        >
          {orgLogo ? (
            <img src={orgLogo} alt="" className="size-full object-cover" />
          ) : (
            <span
              className="home-display"
              style={{
                fontWeight: 800,
                fontSize: 16,
                letterSpacing: 0.4,
                color: "var(--color-pitch-lime)",
              }}
            >
              {crestInitials}
            </span>
          )}
        </div>
      )}

      <div className="min-w-0 flex-1">
        <div
          className="home-display truncate text-[17px] font-bold sm:text-[20px]"
          style={{ color: "#f4f5f3" }}
        >
          {title}
        </div>
        {resolvedSubtitle && (
          <div
            className="truncate text-[11px] uppercase tracking-[0.14em] sm:text-[12px]"
            style={{ color: "var(--color-on-surface-variant)" }}
          >
            {resolvedSubtitle}
          </div>
        )}
      </div>
    </>
  );
}
